import { useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";

import { gridSpacing } from "@utils";

const MIN_HEIGHT = 300;
const HISTORY_MS = 500;
const SCROLLS = /(auto|scroll|overlay)/;

// The app scrolls inside a full-height layout container rather than the window
const findPageScroller = (element: HTMLElement | null) => {
  for (let node = element; node; node = node.parentElement) {
    if (!SCROLLS.test(getComputedStyle(node).overflowY)) continue;
    const overflows = node.scrollHeight > node.clientHeight + 1;
    const reachesBottom =
      node.getBoundingClientRect().bottom >= window.innerHeight - 1;
    if (overflows || reachesBottom) return node;
  }
  return null;
};

const bottomEdge = (style: CSSStyleDeclaration) =>
  (parseFloat(style.paddingBottom) || 0) +
  (parseFloat(style.borderBottomWidth) || 0);

// Space the layout keeps under the table: its own bottom margin and border, and every wrapper's bottom padding, border and margin up to the scroller
export const spaceBelow = (
  start: HTMLElement,
  scroller: HTMLElement | null,
) => {
  let total = 0;
  for (
    let node: HTMLElement | null = start;
    node && node !== scroller;
    node = node.parentElement
  ) {
    const style = getComputedStyle(node);
    total += (parseFloat(style.marginBottom) || 0) + bottomEdge(style);
  }
  return scroller ? total + bottomEdge(getComputedStyle(scroller)) : total;
};

/**
 * A new height can change which element scrolls, which can give back the previous height and loop forever;
 * when the measurement flips back to the height before last, settle on the smaller of the two
 */
export const settleHeight = (applied: number[], measured: number) => {
  const last = applied.at(-1);
  const beforeLast = applied.at(-2);
  const height =
    last !== undefined && measured !== last && measured === beforeLast
      ? Math.min(measured, last)
      : measured;
  return {
    height,
    applied: height === last ? applied : [...applied.slice(-1), height],
  };
};

// Max height that keeps the grid, and whatever sits below it, inside the page's visible height
export const useFillHeight = ({
  enabled,
  gridRef,
  belowRef,
  containerRef,
}: {
  enabled: boolean;
  gridRef: RefObject<HTMLElement | null>;
  belowRef: RefObject<HTMLElement | null>;
  containerRef?: RefObject<HTMLElement | null>;
}) => {
  const [height, setHeight] = useState<number>();
  // Heights applied since the last window resize, to catch a layout flipping between two answers
  const applied = useRef<number[]>([]);
  const lastApplied = useRef(0);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!enabled || !grid) {
      setHeight(undefined);
      return;
    }
    const scroller = findPageScroller(grid.parentElement);
    const measure = () => {
      // Offset within the scrolled content, so scrolling the page doesn't change it
      const top = scroller
        ? grid.getBoundingClientRect().top -
          scroller.getBoundingClientRect().top +
          scroller.scrollTop
        : grid.getBoundingClientRect().top + window.scrollY;
      const visible = scroller ? scroller.clientHeight : window.innerHeight;
      const below = belowRef.current?.offsetHeight ?? 0;
      const gap = Math.max(
        spaceBelow(containerRef?.current ?? grid, scroller),
        gridSpacing[4],
      );
      // A layout loop repeats within frames; anything slower is a real change, e.g. content above growing back
      const now = performance.now();
      if (now - lastApplied.current > HISTORY_MS) applied.current = [];
      lastApplied.current = now;
      const settled = settleHeight(
        applied.current,
        Math.max(MIN_HEIGHT, Math.floor(visible - top - below - gap)),
      );
      applied.current = settled.applied;
      setHeight((current) =>
        current === settled.height ? current : settled.height,
      );
    };
    measure();

    let frame = 0;
    const remeasure = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };
    const onResize = () => {
      applied.current = [];
      remeasure();
    };
    window.addEventListener("resize", onResize);
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(remeasure);
    // The table's ancestors change size when content above or around it does
    for (
      let node = grid.parentElement;
      node && node !== scroller;
      node = node.parentElement
    ) {
      observer?.observe(node);
    }
    if (scroller) observer?.observe(scroller);
    if (belowRef.current) observer?.observe(belowRef.current);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
      observer?.disconnect();
    };
  }, [enabled]);

  return enabled ? height : undefined;
};
